import React, { useEffect, useState, useMemo } from "react";
import { FollowerNode, ChannelEvents } from "@local-logic/channel";
import type { EventPayload } from "@local-logic/channel";
import { useSearch, useNavigate, useLocation } from "@tanstack/react-location";
import type { MakeGenerics } from "@tanstack/react-location";
import once from "lodash.once";
import i18n from "../../i18n";
import defaultState from "./defaultState";
import { primitiveUtils } from "~/utils";
import useEffectOnce from "~/hooks/useEffectOnce";
import useDebounce from "~/hooks/useDebounce";
import { SDKProps, DefaultContext } from "~/types";
import { SDKs } from "~/constants/routes";
import errorTracker from "~/utils/errorTracker";

// Using once to get around generic Context issue: https://stackoverflow.com/a/61020816/4282290
const createStateManagerContext = once(<T extends SDKProps>() =>
  React.createContext<DefaultContext<T>>({} as DefaultContext<T>),
);

const useStateManagerContext = <T extends SDKProps>() => {
  const context = React.useContext(createStateManagerContext<T>());

  if (!context) {
    throw new Error(
      "useStateManagerContext must be used under StateManagerProvider",
    );
  }

  return context;
};

/**
 * Formats the state so that clientId is always extracted from the apiKey and
 * "V3 " prefix is optional in the "apiKey" itself when implementing the SDK.
 */
function formatSDKState<T extends SDKProps>(
  state: DefaultContext<T>["sdkState"],
): DefaultContext<T>["sdkState"] {
  let noPrefixApiKey = state.apiKey || "";

  if (noPrefixApiKey.startsWith("V3 ")) {
    noPrefixApiKey = noPrefixApiKey.slice(3);
  }

  const newState = {
    ...state,
    apiKey: `V3 ${noPrefixApiKey}`,
    clientId: noPrefixApiKey.split(".")[0],
  };

  if (
    typeof state?.options?.lat === "string" ||
    typeof state?.options?.lng === "string"
  ) {
    if (!newState.options) {
      newState.options = {};
    }

    newState.options.lat = Number(state?.options?.lat);
    newState.options.lng = Number(state?.options?.lng);

    // This patch is for LocalContent
    // @ts-expect-error marker exists in runtime but not in generic type
    if (typeof state?.options?.marker !== "undefined") {
      let convertedMarkerToNumber = false;
      // @ts-expect-error marker exists in runtime but not in generic type
      if (typeof state.options.marker.lat === "string") {
        // @ts-expect-error marker exists in runtime but not in generic type
        newState.options.marker.lat = Number(state.options.marker.lat);
        convertedMarkerToNumber = true;
      }

      // @ts-expect-error marker exists in runtime but not in generic type
      if (typeof state.options.marker.lng === "string") {
        // @ts-expect-error marker exists in runtime but not in generic type
        newState.options.marker.lng = Number(state.options.marker.lng);
        convertedMarkerToNumber = true;
      }

      if (convertedMarkerToNumber) {
        console.warn(
          "[Local Logic SDK] Converted lat/lng from string to number in sdkState.options.marker. Please ensure lat/lng are always numbers. This may break your SDK implementation in the future.",
        );
      }
    }

    console.warn(
      "[Local Logic SDK] Converted lat/lng from string to number in sdkState.options. Please ensure lat/lng are always numbers. This may break your SDK implementation in the future.",
    );
  }

  return newState;
}

const StateManagerProvider = React.memo(
  <T extends SDKProps>({ children }: { children: React.ReactNode }) => {
    const StateManagerContext = createStateManagerContext<T>();
    const search = useSearch<
      MakeGenerics<{
        Search: DefaultContext<T>["sdkState"] & { channelId: string };
      }>
    >() as DefaultContext<T>["sdkState"] & { channelId: string };
    const sdkStateRef = React.useRef(search) as React.MutableRefObject<
      DefaultContext<T>["sdkState"]
    >;
    const location = useLocation();
    const pathSegments = location.current.pathname.split("/");
    const currentRoute = pathSegments[pathSegments.length - 1] as SDKs;
    const defaultSDKState = defaultState[
      currentRoute
    ] as DefaultContext<T>["sdkState"];

    const followerNodeRef = React.useRef<FollowerNode>();
    const [isConnected, setIsConnected] = useState(false);
    const [sdkState, _setSdkState] = useState<DefaultContext<T>["sdkState"]>(
      formatSDKState(defaultSDKState),
    );

    const debouncedSdkState = useDebounce(sdkState, 500);
    const navigate = useNavigate();

    const setSdkState = (data: DefaultContext<T>["sdkState"]) => {
      sdkStateRef.current = formatSDKState(data);
      _setSdkState(formatSDKState(data));
    };

    const handleMessage = (e: EventPayload) => {
      if (e.name === ChannelEvents.GET_STATE) {
        followerNodeRef.current?.message(
          ChannelEvents.GET_STATE_RESPONSE,
          sdkStateRef.current,
          e.eventId,
        );
      }

      if (e.name === ChannelEvents.SET_STATE) {
        setSdkState(e.data as DefaultContext<T>["sdkState"]);
      }
    };

    /**
     * The `.connect()` event should only every be triggered once.
     */
    useEffectOnce(() => {
      const parentOrigin = new URLSearchParams(window.location.search).get(
        "parentOrigin",
      );

      const followerNode = new FollowerNode(search.channelId, {
        targetOrigin: parentOrigin || document.referrer,
        window: window.parent,
      });

      followerNodeRef.current = followerNode;

      followerNode?.onMessage(handleMessage);

      followerNode?.connect().then((resp) => {
        const mergedState = primitiveUtils.object.mergeDeep(
          sdkState,
          resp.data as DefaultContext<T>["sdkState"],
        );
        setSdkState(mergedState);
        setIsConnected(true);

        errorTracker.addBreadCrumb(
          "stateManager",
          "useEffectOnce: Connected to sdks-js",
          {
            oldState: sdkState,
            newState: resp.data,
            mergedState,
          },
        );
      });
    });

    const onChange: DefaultContext<T>["onChange"] = (e: unknown) => {
      followerNodeRef.current?.message(ChannelEvents.ON_CHANGE, e);

      errorTracker.addBreadCrumb("stateManager", "onChange", {
        data: e,
      });
    };

    useEffect(() => {
      navigate({
        search: () => debouncedSdkState,
        replace: true,
      });
    }, [debouncedSdkState]);

    useEffect(() => {
      if (sdkState.options) {
        onChange({ type: "options", data: sdkState.options });
      }
    }, [sdkState.options]);

    const contextValue = useMemo(
      () => ({
        sdkState,
        setSdkState,
        onChange,
        followerNode: followerNodeRef.current,
      }),
      [sdkState, setSdkState, onChange],
    );

    useEffect(() => {
      // Avoids unecessarily setting the language multiple times
      if (!isConnected || !sdkState.globalOptions?.locale) {
        return;
      }

      const language = sdkState.globalOptions?.locale || "en";

      if (i18n.language !== language) {
        errorTracker.addBreadCrumb("stateManager", "i18n.changeLanguage", {
          language,
        });

        i18n.changeLanguage(language);
      }
    }, [sdkState.globalOptions?.locale, isConnected]);

    if (!isConnected) {
      return null;
    }

    return (
      <StateManagerContext.Provider value={contextValue}>
        {children}
      </StateManagerContext.Provider>
    );
  },
);

StateManagerProvider.displayName = "StateManagerProvider";

export { useStateManagerContext, StateManagerProvider };
